/**
 * GrcEtatCivilPage — item 13.1. Le SUIVI des dossiers d'état civil (mariage,
 * PACS, parrainage civil, décès) : checklist de pièces, dates clés, statut.
 *
 * La ligne rouge est côté serveur (voir mig 290) : cet écran ne montre ni ne
 * saisit jamais un acte — le registre reste dans le logiciel homologué.
 * Le bouton « Rendez-vous » (D5, V1 validée le 02/08) ouvre le module RDV
 * existant : audition de mariage, dépôt de PACS s'y planifient.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { GrcNav } from '@/components/GrcNav';
import { Alert, Badge, Button, Card, Field, Input } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException, type EtatCivilDossier, type EtatCivilStatut, type EtatCivilType } from '@/lib/api';
import { BookHeart, Plus, Pencil, Trash2, CalendarClock, CheckCircle2, Circle } from 'lucide-react';

const TYPES: { k: EtatCivilType; label: string }[] = [
  { k: 'mariage', label: 'Mariage' },
  { k: 'pacs', label: 'PACS' },
  { k: 'parrainage_civil', label: 'Parrainage civil' },
  { k: 'deces', label: 'Décès' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.k, t.label])) as Record<EtatCivilType, string>;

const STATUT_LABEL: Record<EtatCivilStatut, string> = {
  constitution: 'En constitution', complet: 'Dossier complet',
  bans_publies: 'Bans publiés', programme: 'Programmé',
  celebre: 'Célébré', clos: 'Clos', abandonne: 'Abandonné',
};
const STATUT_BADGE: Record<EtatCivilStatut, 'neutral' | 'info' | 'success' | 'warning'> = {
  constitution: 'neutral', complet: 'info', bans_publies: 'info',
  programme: 'warning', celebre: 'success', clos: 'neutral', abandonne: 'neutral',
};

export default function GrcEtatCivilPage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [dossiers, setDossiers] = useState<EtatCivilDossier[]>([]);
  const [filtreType, setFiltreType] = useState<EtatCivilType | ''>('');
  const [actifs, setActifs] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [edit, setEdit] = useState<EtatCivilDossier | 'new' | null>(null);

  async function reload() {
    try {
      const r = await api.etatCivil.list({ type: filtreType || undefined, actifs });
      setDossiers(r.dossiers);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiException ? e.message : 'Chargement impossible');
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [filtreType, actifs]);

  /** Cocher une pièce depuis la liste : le geste le plus fréquent du guichet. */
  async function togglePiece(d: EtatCivilDossier, index: number) {
    const pieces = d.pieces.map((p, i) => i === index ? { ...p, recue: !p.recue } : p);
    try {
      await api.etatCivil.update(d.id, { ...d, pieces });
      await reload();
    } catch (e) {
      setErreur(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    }
  }

  async function supprimer(d: EtatCivilDossier) {
    const ok = await confirm({
      title: 'Supprimer ce dossier ?',
      description: `« ${d.intitule} » et sa checklist seront supprimés. Le registre officiel n'est pas concerné.`,
      confirmLabel: 'Supprimer', danger: true,
    });
    if (!ok) return;
    try { await api.etatCivil.remove(d.id); await reload(); }
    catch (e) { setErreur(e instanceof ApiException ? e.message : 'Suppression impossible'); }
  }

  return (
    <div className="min-h-screen bg-bg">
      <AppHeader module="GRC & communication" accent="grc" />
      <GrcNav active="dossiers" />
      <main className="mx-auto max-w-4xl px-4 py-6">
        <div className="mb-1 flex items-center gap-2">
          <BookHeart className="h-6 w-6 text-grc" aria-hidden="true" />
          <h1 className="font-display text-xl font-semibold text-ink">Dossiers d'état civil</h1>
        </div>
        <p className="mb-4 text-sm text-ink-muted">
          Suivi des pièces, des dates et des rendez-vous — mariage, PACS,
          parrainage civil, décès. <strong>Le registre d'état civil reste tenu
          dans le logiciel homologué</strong> : rien ici n'y touche.
        </p>

        {erreur && <Alert variant="error" className="mb-4">{erreur}</Alert>}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={filtreType}
            onChange={(e) => setFiltreType(e.target.value as EtatCivilType | '')}
            className="rounded-r-lg border border-border bg-surface px-3 py-1.5 text-sm text-ink"
            aria-label="Filtrer par type"
          >
            <option value="">Tous les types</option>
            {TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm text-ink-muted">
            <input type="checkbox" checked={actifs} onChange={(e) => setActifs(e.target.checked)} />
            En cours seulement
          </label>
          <div className="ml-auto flex gap-2">
            <Link to="/grc/services">
              <Button variant="secondary" size="sm">
                <CalendarClock className="h-4 w-4" aria-hidden="true" /> Rendez-vous
              </Button>
            </Link>
            <Button variant="primary" size="sm" onClick={() => setEdit('new')}>
              <Plus className="h-4 w-4" aria-hidden="true" /> Nouveau dossier
            </Button>
          </div>
        </div>

        {dossiers.length === 0 ? (
          <Card variant="glass" padded>
            <p className="text-sm text-ink-muted">Aucun dossier{actifs ? ' en cours' : ''}.</p>
          </Card>
        ) : (
          <ul className="space-y-3">
            {dossiers.map((d) => {
              const recues = d.pieces.filter((p) => p.recue).length;
              return (
                <li key={d.id}>
                  <Card variant="glass" padded>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-ink">{d.intitule}</span>
                          <Badge variant="neutral" size="sm">{TYPE_LABEL[d.type]}</Badge>
                          <Badge variant={STATUT_BADGE[d.statut]} size="sm">{STATUT_LABEL[d.statut]}</Badge>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-ink-dim">
                          {d.pieces.length > 0 && <span>Pièces : {recues}/{d.pieces.length}</span>}
                          {d.bansPubliesLe && <span>Bans publiés le {d.bansPubliesLe}</span>}
                          {d.dateEvenement && <span>Date prévue : {d.dateEvenement}</span>}
                        </div>
                        {d.pieces.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {d.pieces.map((p, i) => (
                              <button
                                key={i} type="button" onClick={() => void togglePiece(d, i)}
                                className={`inline-flex items-center gap-1 rounded-r-pill border px-2.5 py-1 text-xs transition-colors ${
                                  p.recue
                                    ? 'border-valide/40 bg-valide-tint/40 text-valide-fg'
                                    : 'border-border bg-bg-2 text-ink-muted hover:border-grc'
                                }`}
                                aria-pressed={p.recue}
                                title={p.recue ? 'Pièce reçue — cliquer pour décocher' : 'Cliquer quand la pièce est reçue'}
                              >
                                {p.recue
                                  ? <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                                  : <Circle className="h-3 w-3" aria-hidden="true" />}
                                {p.libelle}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEdit(d)} aria-label={`Modifier ${d.intitule}`}>
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => void supprimer(d)} aria-label={`Supprimer ${d.intitule}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}

        {edit && (
          <DossierDialog
            initial={edit === 'new' ? null : edit}
            onClose={() => setEdit(null)}
            onSaved={() => { setEdit(null); void reload(); }}
            onError={setErreur}
          />
        )}
        {dialogElement}
      </main>
    </div>
  );
}

function DossierDialog({ initial, onClose, onSaved, onError }: {
  initial: EtatCivilDossier | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [type, setType] = useState<EtatCivilType>(initial?.type ?? 'mariage');
  const [intitule, setIntitule] = useState(initial?.intitule ?? '');
  const [statut, setStatut] = useState<EtatCivilStatut>(initial?.statut ?? 'constitution');
  const [bansPubliesLe, setBans] = useState(initial?.bansPubliesLe ?? '');
  const [dateEvenement, setDateEvt] = useState(initial?.dateEvenement ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [pieces, setPieces] = useState(initial?.pieces ?? []);
  const [pieceInput, setPieceInput] = useState('');
  const [busy, setBusy] = useState(false);

  // Le statut ne se choisit que parmi l'état courant + ses transitions permises
  // — la liste vient du SERVEUR, pas d'une seconde copie de la machine à états.
  const statutsProposes: EtatCivilStatut[] = initial
    ? [initial.statut, ...initial.transitionsDisponibles]
    : ['constitution'];

  function addPiece() {
    const v = pieceInput.trim();
    if (!v) return;
    setPieces((p) => [...p, { libelle: v, recue: false }]);
    setPieceInput('');
  }

  async function save() {
    if (!intitule.trim()) { onError('L\'intitulé est requis'); return; }
    setBusy(true);
    const body = {
      type, intitule: intitule.trim(), statut, pieces,
      interesses: initial?.interesses ?? [],
      bansPubliesLe: bansPubliesLe || null,
      dateEvenement: dateEvenement || null,
      notes: notes.trim() || null,
    };
    try {
      if (initial) await api.etatCivil.update(initial.id, body);
      else await api.etatCivil.create(body);
      onSaved();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setBusy(false); }
  }

  return (
    <Dialog
      open onClose={onClose}
      title={initial ? 'Modifier le dossier' : 'Nouveau dossier'}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type" htmlFor="ec-type" required>
            <select
              id="ec-type" value={type} disabled={Boolean(initial)}
              onChange={(e) => setType(e.target.value as EtatCivilType)}
              className="w-full rounded-r-lg border border-border bg-surface px-3 py-2 text-sm text-ink disabled:opacity-60"
            >
              {TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Statut" htmlFor="ec-statut">
            <select
              id="ec-statut" value={statut}
              onChange={(e) => setStatut(e.target.value as EtatCivilStatut)}
              className="w-full rounded-r-lg border border-border bg-surface px-3 py-2 text-sm text-ink"
            >
              {statutsProposes.map((st) => <option key={st} value={st}>{STATUT_LABEL[st]}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Intitulé" htmlFor="ec-intitule" required>
          <Input id="ec-intitule" value={intitule} onChange={(e) => setIntitule(e.target.value)}
                 placeholder="Mariage DUPONT / MARTIN" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          {type === 'mariage' && (
            <Field label="Bans publiés le" htmlFor="ec-bans"
                   hint="Déclenche le rappel du délai légal (10 jours).">
              <Input id="ec-bans" type="date" value={bansPubliesLe ?? ''} onChange={(e) => setBans(e.target.value)} />
            </Field>
          )}
          <Field label={type === 'deces' ? 'Date des obsèques' : 'Date prévue'} htmlFor="ec-evt">
            <Input id="ec-evt" type="date" value={dateEvenement ?? ''} onChange={(e) => setDateEvt(e.target.value)} />
          </Field>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Pièces à réunir</label>
          <div className="flex gap-2">
            <Input value={pieceInput} onChange={(e) => setPieceInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPiece(); } }}
              placeholder="Acte de naissance de moins de 3 mois…" />
            <Button variant="secondary" onClick={addPiece}>Ajouter</Button>
          </div>
          {pieces.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {pieces.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-r-pill border border-border bg-bg-2 px-2.5 py-1 text-xs text-ink-muted">
                  {p.libelle}
                  <button type="button" aria-label="Retirer"
                          onClick={() => setPieces((x) => x.filter((_, j) => j !== i))}
                          className="text-ink-dim hover:text-urgent">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <Field label="Notes internes" htmlFor="ec-notes">
          <textarea id="ec-notes" rows={2} value={notes ?? ''} onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-r-lg border border-border bg-surface px-3 py-2 text-sm text-ink" />
        </Field>
      </div>
    </Dialog>
  );
}
